import { AppointmentSlotDuration, getAvailabilitySlotsForRange, GetAvailabilitySlotsForRangeInput } from '@uplift/api';
import { useTimezone } from '@uplift/contexts';
import { CalendarDateService } from '@uplift/services';
import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';


export const useAvailability = (params: {
  providerIds: string[];
  patientId: string;
  bookingSlotDuration?: AppointmentSlotDuration;
  bookingSource?: GetAvailabilitySlotsForRangeInput['booking_source'];
}) => {
  const timezone = useTimezone();

  const { from, to } = useMemo(() => {
    const from = CalendarDateService.date().tz(timezone).startOf('day').utc();
    const to = from.add(30 + 1, 'days');

    return {
      from: from.format(),
      to: to.format(),
    };
  }, [ timezone ]);

  const slotQueries = useQueries({
    queries: params.providerIds?.map(providerId => ({
      queryKey: [ 'availability-slots', providerId, params.patientId, from, to, timezone, params.bookingSlotDuration ],
      queryFn: async () => ({
        providerId: providerId,
        slots: await getAvailabilitySlotsForRange({
          datetime_from: from,
          datetime_to: to,
          provider_id: providerId,
          patient_id: params.patientId,
          booking_slot_duration: params.bookingSlotDuration,
          booking_source: params.bookingSource,
        }),
      })
    })) ?? []
  });

  const slots = useMemo(
    () => slotQueries.map(x => ({
      providerId: x?.data?.providerId,
      slots: x?.data?.slots?.slots.map(s => CalendarDateService.date(s).utc().format()) ?? []
    })),
    [ slotQueries ]
  );

  return {
    slotsLoading: slotQueries.some(x => x.isLoading),
    slots,
    refetchSlots: (providerId?: string) => {
      slotQueries.forEach(x => {
        if (providerId && x.data?.providerId !== providerId) {
          return;
        }
        x.refetch();
      });
    },
  };
};
